import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Paper, Typography, IconButton, Select, MenuItem, Box, Chip,
  Tabs, Tab, Card, CardContent, LinearProgress, Accordion,
  AccordionSummary, AccordionDetails
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { ArrowBack, ExpandMore } from '@mui/icons-material';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PersonIcon from '@mui/icons-material/Person';
import HubIcon from '@mui/icons-material/Hub';
import AppContext from '../components/AppContext';
import { getResumTransversal } from '../services/concepteGraphService';
import { listAnys } from '../functions/common-lists';
import { formatEuro } from '../functions/formatNumber';
import { getColorForAmount } from '../functions/colors';
import { ConcepteIcon } from '../components/common/IconMap';
import useBack from '../hooks/useBack';

const EntityCard = ({ item, selectedYear, userapp }) => {
  const navigate = useNavigate();
  const isVehicle = item.tipusNode === 'VEHICLE';
  const despesa = Math.abs(item.totalDespesa || 0);
  const ingres = item.totalIngres || 0;
  const saldo = ingres - despesa;
  const colorSaldo = getColorForAmount(saldo, false);

  const desglossament = item.desglossamentPerCategoria || {};
  const categoriesList = Object.entries(desglossament).sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]));

  const handleGotoConcepte = (concepteNom) => {
    if (selectedYear) {
      navigate(`/${userapp}/dashboard/${selectedYear}/concepte/${concepteNom}`);
    } else {
      navigate(`/${userapp}/dashboard/concepte/${concepteNom}`);
    }
  };

  return (
    <Card elevation={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isVehicle ? (
              <DirectionsCarIcon sx={{ color: '#0288D1', fontSize: 28 }} />
            ) : (
              <PersonIcon sx={{ color: '#7B1FA2', fontSize: 28 }} />
            )}
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
                {item.nom}
              </Typography>
            </Box>
          </Box>
          <Chip
            label={isVehicle ? 'Vehicle' : 'Persona'}
            size="small"
            sx={{
              backgroundColor: isVehicle ? '#e1f5fe' : '#f3e5f5',
              color: isVehicle ? '#0288D1' : '#7B1FA2',
              fontWeight: 600
            }}
          />
        </Box>

        <Grid container spacing={1} sx={{ mb: 2, bgcolor: '#fbfcfd', p: 1, borderRadius: 1 }}>
          <Grid size={4}>
            <Typography variant="caption" color="text.secondary">Despesa</Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'error.main' }}>
              {formatEuro(despesa)}
            </Typography>
          </Grid>
          <Grid size={4}>
            <Typography variant="caption" color="text.secondary">Ingrés</Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
              {formatEuro(ingres)}
            </Typography>
          </Grid>
          <Grid size={4}>
            <Typography variant="caption" color="text.secondary">Net</Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: colorSaldo }}>
              {formatEuro(saldo)}
            </Typography>
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {item.numMoviments || 0} moviments totals
          </Typography>
          <Typography
            variant="caption"
            onClick={() => handleGotoConcepte(item.nom)}
            sx={{ color: 'primary.main', cursor: 'pointer', textDecoration: 'underline' }}
          >
            Veure resum del concepte &rarr;
          </Typography>
        </Box>
      </CardContent>

      {/* Desglossament per Categoria */}
      <Box sx={{ mt: 'auto', borderTop: '1px solid #f0f0f0' }}>
        <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMore />} sx={{ minHeight: 36, py: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              Desglossament per categoria ({categoriesList.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, pb: 1.5 }}>
            {categoriesList.length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                Sense moviments associats.
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                {categoriesList.map(([catNom, importVal]) => {
                  const catDespesa = Math.abs(importVal);
                  const pct = despesa > 0 ? Math.round((catDespesa / despesa) * 100) : 0;
                  return (
                    <Box key={catNom} sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.8, cursor: 'pointer' }}
                          onClick={() => handleGotoConcepte(catNom)}
                        >
                          <ConcepteIcon concept={catNom} onlyIcon sx={{ fontSize: 16 }} />
                          <Typography variant="caption" sx={{ fontWeight: 500 }}>
                            {catNom}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                            {formatEuro(catDespesa)}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary', ml: 0.5 }}>
                            ({pct}%)
                          </Typography>
                        </Box>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={pct}
                        sx={{ height: 3, borderRadius: 1.5, mt: 0.3, bgcolor: '#f0f0f0' }}
                        color={isVehicle ? 'info' : 'secondary'}
                      />
                    </Box>
                  );
                })}
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      </Box>
    </Card>
  );
};

const DashboardTransversalPage = () => {
  const { userapp, anyo: paramAnyo } = useParams();
  const { ctxUser } = useContext(AppContext);
  const jwtToken = ctxUser?.fdvToken?.jwtToken;
  const navigate = useNavigate();
  const { onBack } = useBack();

  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(paramAnyo ? parseInt(paramAnyo, 10) : currentYear);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabFilter, setTabFilter] = useState(0); // 0: Tots, 1: Vehicles, 2: Persones

  useEffect(() => {
    if (paramAnyo) {
      setSelectedYear(parseInt(paramAnyo, 10));
    }
  }, [paramAnyo]);

  useEffect(() => {
    if (!userapp) return;
    setLoading(true);
    getResumTransversal(jwtToken, userapp, selectedYear || undefined)
      .then((data) => {
        setItems(data || []);
      })
      .catch((err) => {
        console.error('Error al carregar resum transversal:', err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [userapp, jwtToken, selectedYear]);

  const handleYearChange = (e) => {
    const newYear = e.target.value;
    setSelectedYear(newYear);
    if (newYear) {
      navigate(`/${userapp}/transversal/${newYear}`);
    } else {
      navigate(`/${userapp}/transversal`);
    }
  };

  const filteredItems = items.filter((item) => {
    if (tabFilter === 1) return item.tipusNode === 'VEHICLE';
    if (tabFilter === 2) return item.tipusNode === 'PERSONA';
    return true;
  });

  const vehicles = items.filter((i) => i.tipusNode === 'VEHICLE');
  const persones = items.filter((i) => i.tipusNode === 'PERSONA');

  const totalDespesaVehicles = vehicles.reduce((acc, i) => acc + Math.abs(i.totalDespesa || 0), 0);
  const totalDespesaPersones = persones.reduce((acc, i) => acc + Math.abs(i.totalDespesa || 0), 0);

  return (
    <Box sx={{ p: 2, maxWidth: 1200, margin: '0 auto' }}>
      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, sm: 8 }} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={onBack}>
            <ArrowBack />
          </IconButton>
          <HubIcon color="primary" />
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            Resum Transversal: Vehicles i Persones
          </Typography>
        </Grid>

        <Grid size={{ xs: 12, sm: 4 }} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Select
            value={selectedYear || ''}
            size="small"
            onChange={handleYearChange}
            displayEmpty
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="">Històric Global</MenuItem>
            {listAnys().map((y) => (
              <MenuItem key={y} value={y}>{y}</MenuItem>
            ))}
          </Select>
        </Grid>
      </Grid>

      {/* Targetes de Totals per Tipus d'Entitat */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Card sx={{ bgcolor: '#f0f9ff', borderLeft: '4px solid #0288D1' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DirectionsCarIcon sx={{ color: '#0288D1' }} />
                  <Typography variant="subtitle1" fontWeight="bold">Total Vehicles ({vehicles.length})</Typography>
                </Box>
                <Typography variant="h6" color="error.main" fontWeight="bold">
                  {formatEuro(totalDespesaVehicles)}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                Suma transversal de manteniment, combustible, assegurances i préstecs
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <Card sx={{ bgcolor: '#faf5ff', borderLeft: '4px solid #7B1FA2' }}>
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon sx={{ color: '#7B1FA2' }} />
                  <Typography variant="subtitle1" fontWeight="bold">Total Membres / Persones ({persones.length})</Typography>
                </Box>
                <Typography variant="h6" color="error.main" fontWeight="bold">
                  {formatEuro(totalDespesaPersones)}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                Suma transversal de despeses personals, roba, oci, salut i activitats
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Pestanyes de Filtre */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={tabFilter}
          onChange={(e, val) => setTabFilter(val)}
          indicatorColor="primary"
          textColor="primary"
          centered
        >
          <Tab label={`Tots (${items.length})`} />
          <Tab label={`Vehicles (${vehicles.length})`} icon={<DirectionsCarIcon fontSize="small" />} iconPosition="start" />
          <Tab label={`Persones / Membres (${persones.length})`} icon={<PersonIcon fontSize="small" />} iconPosition="start" />
        </Tabs>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Grid d'Entitats */}
      <Grid container spacing={2}>
        {filteredItems.length === 0 && !loading && (
          <Grid size={12}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                No s'han trobat entitats transversals per a aquest període.
              </Typography>
            </Paper>
          </Grid>
        )}

        {filteredItems.map((item) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={item.id || item.codi}>
            <EntityCard item={item} selectedYear={selectedYear} userapp={userapp} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default DashboardTransversalPage;
